import React from 'react';
import { useTranslation } from 'react-i18next';

const BacktestResultsPage = ({ currentTab }) => {
  const { t } = useTranslation();

  // Render Hyper v3.0.1 results
  const renderHyperV3 = () => (
    <div className="content-section">
      <h2>{t('backtesting.hyperV3.title')}</h2>
      <div className="backtesting-content">
        <div className="results-intro">
          <p>{t('backtesting.hyperV3.intro')}</p>
        </div>
        
        {/* First test period (2011-2018) */}
        <div className="test-period-section">
          <h3>{t('backtesting.hyperV3.period1.title')}</h3>
          
          <div className="backtest-metrics-grid">
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.cagr')}</div>
              <div className="metric-value">14.22%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 9.81%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.sharpeRatio')}</div>
              <div className="metric-value">1.28</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 0.86</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.maxDrawdown')}</div>
              <div className="metric-value">-17.23%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: -18.61%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.sortinoRatio')}</div>
              <div className="metric-value">1.83</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 1.15</div>
            </div>
          </div>

          <div className="backtest-metrics-grid">
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.cumulativeReturn')}</div>
              <div className="metric-value">192.44%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 107.38%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.winDays')}</div>
              <div className="metric-value">54.15%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 53.93%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.bestYear')}</div>
              <div className="metric-value">32.76%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 26.41%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.worstYear')}</div>
              <div className="metric-value">5.84%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 1.36%</div>
            </div>
          </div>
          
          <div className="results-card-container">
            <div className="results-card">
              <h4>{t('backtesting.cards.performanceTearsheet')}</h4>
              <p>{t('backtesting.cards.tearsheetDescription')}</p>
              <div className="file-access">
                <a 
                  href="/tearsheets/Hyper_v3.0.1_batch2_test1_2011_2018_backtest.html"
                  download="AmpyFin_Hyper_Model_v3.0.1_Bull_Market_Tearsheet_2011-2018.html"
                  className="file-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('backtesting.buttons.downloadTearsheet')}
                </a>
              </div>
            </div>
            
            <div className="results-card">
              <h4>{t('backtesting.cards.tradeSimulator')}</h4>
              <p>{t('backtesting.cards.logDescription')}</p>
              <div className="file-access">
                <a 
                  href="/logs/Hyper_v3.0.1_batch2_test1_2011_2018_log.zip"
                  download
                  className="file-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('backtesting.buttons.downloadLog')}
                </a>
              </div>
            </div>
          </div>
          
          <div className="methodology-note">
            <h4>{t('backtesting.methodology.title')}</h4>
            <p>{t('backtesting.methodology.description')}</p>
            <ul>
              <li><strong>{t('backtesting.methodology.timePeriod')}:</strong> {t('backtesting.hyperV3.period1.timePeriod')}</li>
              <li><strong>{t('backtesting.methodology.marketCondition')}:</strong> {t('backtesting.hyperV3.period1.marketCondition')}</li>
              <li><strong>{t('backtesting.methodology.dataSource')}:</strong> {t('backtesting.methodology.dataSourceDescription')}</li>
              <li><strong>{t('backtesting.methodology.computation')}:</strong> {t('backtesting.methodology.computationDescription')}</li>
              <li><strong>{t('backtesting.methodology.transactionCosts')}:</strong> {t('backtesting.methodology.transactionCostsDescription')}</li>
              <li><strong>{t('backtesting.methodology.modelVersion')}:</strong> {t('backtesting.hyperV3.modelVersion')}</li>
            </ul>
          </div>
        </div>
        
        {/* Second test period (2003-2007) */}
        <div className="test-period-section">
          <h3>{t('backtesting.hyperV3.period2.title')}</h3>
          
          <div className="backtest-metrics-grid">
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.cagr')}</div>
              <div className="metric-value">8.64%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: -2.02%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.sharpeRatio')}</div>
              <div className="metric-value">1.24</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 0.94</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.maxDrawdown')}</div>
              <div className="metric-value">-16.35%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: -9.05%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.winRate')}</div>
              <div className="metric-value">53.05%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 52.77%</div>
            </div>
          </div>
          
          <div className="results-card-container">
            <div className="results-card">
              <h4>{t('backtesting.cards.performanceTearsheet')}</h4>
              <p>{t('backtesting.cards.tearsheetDescription')}</p>
              <div className="file-access">
                <a 
                  href="/tearsheets/Hyper_v3.0.1_batch2_test1_2003_2007_backtest.html"
                  download="AmpyFin_Hyper_Model_v3.0.1_Bull_Market_Tearsheet_2003-2007.html"
                  className="file-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('backtesting.buttons.downloadTearsheet')}
                </a>
              </div>
            </div>
            
            <div className="results-card">
              <h4>{t('backtesting.cards.tradeSimulator')}</h4>
              <p>{t('backtesting.cards.logDescription')}</p>
              <div className="file-access">
                <a 
                  href="/logs/Hyper_v3.0.1_batch2_test1_2003_2007_log.zip"
                  download
                  className="file-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('backtesting.buttons.downloadLog')}
                </a>
              </div>
            </div>
          </div>
          
          <div className="methodology-note">
            <h4>{t('backtesting.methodology.title')}</h4>
            <p>{t('backtesting.methodology.description')}</p>
            <ul>
              <li><strong>{t('backtesting.methodology.timePeriod')}:</strong> {t('backtesting.hyperV3.period2.timePeriod')}</li>
              <li><strong>{t('backtesting.methodology.marketCondition')}:</strong> {t('backtesting.hyperV3.period2.marketCondition')}</li>
              <li><strong>{t('backtesting.methodology.dataSource')}:</strong> {t('backtesting.methodology.dataSourceDescription')}</li>
              <li><strong>{t('backtesting.methodology.computation')}:</strong> {t('backtesting.methodology.computationDescription')}</li>
              <li><strong>{t('backtesting.methodology.transactionCosts')}:</strong> {t('backtesting.methodology.transactionCostsDescription')}</li>
              <li><strong>{t('backtesting.methodology.modelVersion')}:</strong> {t('backtesting.hyperV3.modelVersion')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Prag v3.0.1 results
  const renderPragV3 = () => (
    <div className="content-section">
      <h2>{t('backtesting.pragV3.title')}</h2>
      <div className="backtesting-content">
        <div className="results-intro">
          <p>{t('backtesting.pragV3.intro')}</p>
        </div>
        
        {/* First test period (2008-2013) - Financial Crisis and Recovery */}
        <div className="test-period-section">
          <h3>{t('backtesting.pragV3.period1.title')}</h3>
          
          <div className="backtest-metrics-grid">
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.cagr')}</div>
              <div className="metric-value">6.78%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 1.55%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.sharpeRatio')}</div>
              <div className="metric-value">0.59</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 0.18</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.maxDrawdown')}</div>
              <div className="metric-value">-29.34%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: -55.15%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.sortinoRatio')}</div>
              <div className="metric-value">0.82</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 0.24</div>
            </div>
          </div>
          
          <div className="backtest-metrics-grid">
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.cumulativeReturn')}</div>
              <div className="metric-value">44.92%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 10.19%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.winDays')}</div>
              <div className="metric-value">50.68%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 48.45%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.volatility')}</div>
              <div className="metric-value">12.37%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 21.18%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.recoveryTime')}</div>
              <div className="metric-value">14.5 months</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 32.2 months</div>
            </div>
          </div>
          
          <div className="results-card-container">
            <div className="results-card">
              <h4>{t('backtesting.cards.performanceTearsheet')}</h4>
              <p>{t('backtesting.cards.tearsheetDescription')}</p>
              <div className="file-access">
                <a 
                  href="/tearsheets/Prag_v3.0.1_batch1_test1_2008_2013_backtest.html"
                  download="AmpyFin_Prag_Model_v3.0.1_Market_Crisis_Tearsheet_2008-2013.html"
                  className="file-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('backtesting.buttons.downloadTearsheet')}
                </a>
              </div>
            </div>
            
            <div className="results-card">
              <h4>{t('backtesting.cards.tradeSimulator')}</h4>
              <p>{t('backtesting.cards.logDescription')}</p>
              <div className="file-access">
                <a 
                  href="/logs/Prag_v3.0.1_batch1_test1_2008_2013_log.zip"
                  download
                  className="file-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('backtesting.buttons.downloadLog')}
                </a>
              </div>
            </div>
          </div>
          
          <div className="methodology-note">
            <h4>{t('backtesting.pragV3.crisisPerformance')}</h4>
            <div className="styled-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('backtesting.tableHeaders.metric')}</th>
                    <th>{t('backtesting.tableHeaders.sp500')}</th>
                    <th>{t('backtesting.tableHeaders.pragV3')}</th>
                    <th>{t('backtesting.tableHeaders.difference')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>2008 Financial Crisis</td>
                    <td>-38.49%</td>
                    <td>-18.72%</td>
                    <td>+19.77%</td>
                  </tr>
                  <tr>
                    <td>2011 Correction</td>
                    <td>-19.39%</td>
                    <td>-13.25%</td>
                    <td>+6.14%</td>
                  </tr>
                  <tr>
                    <td>Avg Crisis Recovery Time</td>
                    <td>24.4 months</td>
                    <td>10.2 months</td>
                    <td>-14.2 months</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="methodology-note">
            <h4>{t('backtesting.methodology.title')}</h4>
            <p>{t('backtesting.methodology.description')}</p>
            <ul>
              <li><strong>{t('backtesting.methodology.timePeriod')}:</strong> {t('backtesting.pragV3.period1.timePeriod')}</li>
              <li><strong>{t('backtesting.methodology.marketCondition')}:</strong> {t('backtesting.pragV3.period1.marketCondition')}</li>
              <li><strong>{t('backtesting.methodology.dataSource')}:</strong> {t('backtesting.methodology.dataSourceDescription')}</li>
              <li><strong>{t('backtesting.methodology.computation')}:</strong> {t('backtesting.methodology.computationDescription')}</li>
              <li><strong>{t('backtesting.methodology.transactionCosts')}:</strong> {t('backtesting.methodology.transactionCostsDescription')}</li>
              <li><strong>{t('backtesting.methodology.modelVersion')}:</strong> {t('backtesting.pragV3.modelVersion')}</li>
            </ul>
          </div>
        </div>
        
        {/* Second test period (2022-2023) - Recent Market Volatility */}
        <div className="test-period-section">
          <h3>{t('backtesting.pragV3.period2.title')}</h3>
          
          <div className="backtest-metrics-grid">
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.cagr')}</div>
              <div className="metric-value">8.64%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: -2.02%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.sharpeRatio')}</div>
              <div className="metric-value">0.68</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: -0.03</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.maxDrawdown')}</div>
              <div className="metric-value">-11.08%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: -23.42%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.winRate')}</div>
              <div className="metric-value">53.03%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 48.76%</div>
            </div>
          </div>
          
          <div className="results-card-container">
            <div className="results-card">
              <h4>{t('backtesting.cards.performanceTearsheet')}</h4>
              <p>{t('backtesting.cards.tearsheetDescription')}</p>
              <div className="file-access">
                <a 
                  href="/tearsheets/Prag_v3.0.1_batch1_test3_2022_2023_backtest.html"
                  download="AmpyFin_Prag_Model_v3.0.1_Recent_Volatility_Tearsheet_2022-2023.html"
                  className="file-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('backtesting.buttons.downloadTearsheet')}
                </a>
              </div>
            </div>
            
            <div className="results-card">
              <h4>{t('backtesting.cards.tradeSimulator')}</h4>
              <p>{t('backtesting.cards.logDescription')}</p>
              <div className="file-access">
                <a 
                  href="/logs/Prag_v3.0.1_batch1_test3_2022_2023_log.zip"
                  download
                  className="file-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('backtesting.buttons.downloadLog')}
                </a>
              </div>
            </div>
          </div>
          
          <div className="methodology-note">
            <h4>{t('backtesting.methodology.title')}</h4>
            <p>{t('backtesting.methodology.description')}</p>
            <ul>
              <li><strong>{t('backtesting.methodology.timePeriod')}:</strong> {t('backtesting.pragV3.period2.timePeriod')}</li>
              <li><strong>{t('backtesting.methodology.marketCondition')}:</strong> {t('backtesting.pragV3.period2.marketCondition')}</li>
              <li><strong>{t('backtesting.methodology.dataSource')}:</strong> {t('backtesting.methodology.dataSourceDescription')}</li>
              <li><strong>{t('backtesting.methodology.computation')}:</strong> {t('backtesting.methodology.computationDescription')}</li>
              <li><strong>{t('backtesting.methodology.transactionCosts')}:</strong> {t('backtesting.methodology.transactionCostsDescription')}</li>
              <li><strong>{t('backtesting.methodology.modelVersion')}:</strong> {t('backtesting.pragV3.modelVersion')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Hyper v4.0.1 results
  const renderHyperV4 = () => (
    <div className="content-section">
      <h2>{t('backtesting.hyperV4.title')}</h2>
      <div className="backtesting-content">
        <div className="results-intro">
          <p>{t('backtesting.hyperV4.intro')}</p>
        </div>
        
        <div className="test-period-section">
          <h3>{t('backtesting.hyperV4.comprehensiveAnalysis')}</h3>
          
          <div className="backtest-metrics-grid">
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.cagr')}</div>
              <div className="metric-value">16.84%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 10.13%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.sharpeRatio')}</div>
              <div className="metric-value">1.5</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 1.0</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.maxDrawdown')}</div>
              <div className="metric-value">-15.65%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: -13.02%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.sortinoRatio')}</div>
              <div className="metric-value">2.26</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 1.41</div>
            </div>
          </div>

          <div className="backtest-metrics-grid">
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.cumulativeReturn')}</div>
              <div className="metric-value">256.37%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 119.95%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.winDays')}</div>
              <div className="metric-value">55.20%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 55.69%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.bestYear')}</div>
              <div className="metric-value">41.83%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 29.29%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.worstYear')}</div>
              <div className="metric-value">9.31%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 1.23%</div>
            </div>
          </div>
          
          <div className="results-card-container">
            <div className="results-card">
              <h4>{t('backtesting.cards.performanceTearsheet')}</h4>
              <p>{t('backtesting.cards.tearsheetDescription')}</p>
              <div className="file-access">
                <a 
                  href="/tearsheets/Hyper_v4.0.1_tearsheet.html"
                  download="AmpyFin_Hyper_Model_v4.0.1_Tearsheet.html"
                  className="file-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('backtesting.buttons.downloadTearsheet')}
                </a>
              </div>
            </div>
            
            <div className="results-card">
              <h4>{t('backtesting.cards.tradeSimulator')}</h4>
              <p>{t('backtesting.cards.logDescription')}</p>
              <div className="file-access">
                <a 
                  href="/logs/Hyper_v4.0.1_test.zip"
                  download
                  className="file-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('backtesting.buttons.downloadLog')}
                </a>
              </div>
            </div>
          </div>
          
          <div className="methodology-note">
            <h4>{t('backtesting.hyperV4.yearlyPerformance')}</h4>
            <div className="styled-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('backtesting.tableHeaders.year')}</th>
                    <th>{t('backtesting.tableHeaders.spy')}</th>
                    <th>{t('backtesting.tableHeaders.strategy')}</th>
                    <th>{t('backtesting.tableHeaders.multiplier')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>2013</td>
                    <td>29.29%</td>
                    <td>35.41%</td>
                    <td>1.21x</td>
                  </tr>
                  <tr>
                    <td>2014</td>
                    <td>13.46%</td>
                    <td>29.47%</td>
                    <td>2.19x</td>
                  </tr>
                  <tr>
                    <td>2015</td>
                    <td>1.23%</td>
                    <td>13.18%</td>
                    <td>10.68x</td>
                  </tr>
                  <tr>
                    <td>2016</td>
                    <td>12.85%</td>
                    <td>15.84%</td>
                    <td>1.23x</td>
                  </tr>
                  <tr>
                    <td>2017</td>
                    <td>21.65%</td>
                    <td>41.83%</td>
                    <td>1.93x</td>
                  </tr>
                  <tr>
                    <td>2018</td>
                    <td>7.88%</td>
                    <td>9.31%</td>
                    <td>1.18x</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="methodology-note">
            <h4>{t('backtesting.hyperV4.improvements')}</h4>
            <p>The Hyper v4.0.1 model includes the following enhancements over previous versions:</p>
            <ul>
              <li><strong>{t('backtesting.improvements.llmIntegration')}:</strong> {t('backtesting.improvements.llmIntegrationDesc')}</li>
              <li><strong>{t('backtesting.improvements.realtimeAdaptation')}:</strong> {t('backtesting.improvements.realtimeAdaptationDesc')}</li>
              <li><strong>{t('backtesting.improvements.enhancedSignal')}:</strong> {t('backtesting.improvements.enhancedSignalDesc')}</li>
              <li><strong>{t('backtesting.improvements.optimizedEntry')}:</strong> {t('backtesting.improvements.optimizedEntryDesc')}</li>
              <li><strong>{t('backtesting.improvements.sectorRotation')}:</strong> {t('backtesting.improvements.sectorRotationDesc')}</li>
              <li><strong>{t('backtesting.methodology.modelVersion')}:</strong> {t('backtesting.hyperV4.modelVersion')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Prag v4.0.1 results
  const renderPragV4 = () => (
    <div className="content-section">
      <h2>{t('backtesting.pragV4.title')}</h2>
      <div className="backtesting-content">
        <div className="results-intro">
          <p>{t('backtesting.pragV4.intro')}</p>
        </div>
        
        <div className="test-period-section">
          <h3>{t('backtesting.pragV4.comprehensiveAnalysis')}</h3>
          
          <div className="backtest-metrics-grid">
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.cagr')}</div>
              <div className="metric-value">10.25%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 1.59%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.sharpeRatio')}</div>
              <div className="metric-value">0.78</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 0.18</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.maxDrawdown')}</div>
              <div className="metric-value">-15.94%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: -24.50%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.sortinoRatio')}</div>
              <div className="metric-value">1.16</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 0.25</div>
            </div>
          </div>

          <div className="backtest-metrics-grid">
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.cumulativeReturn')}</div>
              <div className="metric-value">34.15%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 4.88%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.volatility')}</div>
              <div className="metric-value">13.64%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 16.20%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.winDays')}</div>
              <div className="metric-value">50.46%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 49.81%</div>
            </div>
            <div className="metric-card">
              <div className="metric-name">{t('backtesting.metrics.bestYear')}</div>
              <div className="metric-value">34.96%</div>
              <div className="metric-context">{t('backtesting.metrics.vsBenchmark')}: 26.27%</div>
            </div>
          </div>
          
          <div className="results-card-container">
            <div className="results-card">
              <h4>{t('backtesting.cards.performanceTearsheet')}</h4>
              <p>{t('backtesting.cards.tearsheetDescription')}</p>
              <div className="file-access">
                <a 
                  href="/tearsheets/Prag_v4.0.1_tearsheet.html"
                  download="AmpyFin_Prag_Model_v4.0.1_Tearsheet.html"
                  className="file-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('backtesting.buttons.downloadTearsheet')}
                </a>
              </div>
            </div>
            
            <div className="results-card">
              <h4>{t('backtesting.cards.tradeSimulator')}</h4>
              <p>{t('backtesting.cards.logDescription')}</p>
              <div className="file-access">
                <a 
                  href="/logs/Prag_v4.0.1_test.zip"
                  download
                  className="file-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('backtesting.buttons.downloadLog')}
                </a>
              </div>
            </div>
          </div>
          
          <div className="methodology-note">
            <h4>{t('backtesting.pragV4.yearlyPerformance')}</h4>
            <div className="styled-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('backtesting.tableHeaders.year')}</th>
                    <th>{t('backtesting.tableHeaders.spy')}</th>
                    <th>{t('backtesting.tableHeaders.strategy')}</th>
                    <th>{t('backtesting.tableHeaders.multiplier')}</th>
                    <th>{t('backtesting.tableHeaders.result')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>2021</td>
                    <td>1.94%</td>
                    <td>-1.98%</td>
                    <td>-1.02x</td>
                    <td>Loss</td>
                  </tr>
                  <tr>
                    <td>2022</td>
                    <td>-18.52%</td>
                    <td>1.41%</td>
                    <td>-0.08x</td>
                    <td>Win</td>
                  </tr>
                  <tr>
                    <td>2023</td>
                    <td>26.27%</td>
                    <td>34.96%</td>
                    <td>1.33x</td>
                    <td>Win</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="methodology-note">
            <h4>{t('backtesting.pragV4.worstDrawdowns')}</h4>
            <div className="styled-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('backtesting.tableHeaders.started')}</th>
                    <th>{t('backtesting.tableHeaders.recovered')}</th>
                    <th>{t('backtesting.tableHeaders.drawdown')}</th>
                    <th>{t('backtesting.tableHeaders.days')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>2021-11-25</td>
                    <td>2022-08-11</td>
                    <td>-15.94%</td>
                    <td>260</td>
                  </tr>
                  <tr>
                    <td>2022-08-19</td>
                    <td>2022-11-09</td>
                    <td>-10.51%</td>
                    <td>83</td>
                  </tr>
                  <tr>
                    <td>2022-12-01</td>
                    <td>2023-04-30</td>
                    <td>-7.84%</td>
                    <td>151</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="methodology-note">
            <h4>{t('backtesting.pragV4.improvements')}</h4>
            <p>The Prag v4.0.1 model includes the following enhancements over previous versions:</p>
            <ul>
              <li><strong>{t('backtesting.improvements.advancedVolatility')}:</strong> {t('backtesting.improvements.advancedVolatilityDesc')}</li>
              <li><strong>{t('backtesting.improvements.improvedHedging')}:</strong> {t('backtesting.improvements.improvedHedgingDesc')}</li>
              <li><strong>{t('backtesting.improvements.sentimentAnalysis')}:</strong> {t('backtesting.improvements.sentimentAnalysisDesc')}</li>
              <li><strong>{t('backtesting.improvements.reducedCorrelation')}:</strong> {t('backtesting.improvements.reducedCorrelationDesc')}</li>
              <li><strong>{t('backtesting.improvements.optionStrategy')}:</strong> {t('backtesting.improvements.optionStrategyDesc')}</li>
              <li><strong>{t('backtesting.methodology.modelVersion')}:</strong> {t('backtesting.pragV4.modelVersion')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Route to appropriate render function based on currentTab
  switch (currentTab) {
    case 'hyper-v3-results':
      return renderHyperV3();
    case 'prag-v3-results':
      return renderPragV3();
    case 'hyper-v4-results':
      return renderHyperV4();
    case 'prag-v4-results':
      return renderPragV4();
    default:
      return (
        <div className="content-section">
          <h2>{t('backtesting.title')}</h2>
          <p>Invalid backtest results page.</p>
        </div>
      );
  }
};

export default BacktestResultsPage;
